import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import type { SourceDate } from "@amber/contracts";
import {
  ActionButton,
  AppLink,
  Avatar,
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  Drawer,
  DrawerSection,
  FailedCallout,
  RadioCards,
  ReasonDialog,
  buttonClass,
  ReasonField,
  formatBytes,
  formatCost,
  formatDateTime,
  formatDuration,
  formatRelative,
  formatShortDate,
  isApiError,
  useReason,
  useToast,
  errorText,
  useCommandMutation,
} from "@amber/ui";
import {
  EXCLUSION_LABELS,
  ITEM_FORM_LABELS,
  MEDIA_TYPE_LABELS,
  SOURCE_DATE_BASIS_LABELS,
  isPublicationProxy,
  type ItemDetail,
} from "../../contracts/index";
import { DateConflictList } from "./DateConflicts";
import { ProgressBar, ProgressLabels } from "./ProgressBar";
import { collectionCommands, itemQuery, AFTER_COLLECTION_CHANGE, ON_COLLECTION_CONFLICT } from "../queries";
import { RecoveryDialog, RecoverySection, RetryDialog, StopDialog } from "./Recovery";
import { ReadinessCell, StatusBadge } from "./StatusCell";

type ExclusionKind = keyof typeof EXCLUSION_LABELS;

const EXCLUSION_OPTIONS: readonly { value: ExclusionKind; label: string; description: string }[] = [
  { value: "not_relevant", label: EXCLUSION_LABELS.not_relevant, description: "No tracked person communicates in it" },
  {
    value: "duplicate_handled_elsewhere",
    label: EXCLUSION_LABELS.duplicate_handled_elsewhere,
    description: "Another item already covers this communication",
  },
  { value: "withdrawn", label: EXCLUSION_LABELS.withdrawn, description: "Added by mistake or no longer wanted" },
];

export function formatSourceDate(date: SourceDate | null): string {
  if (!date) return "—";
  if (date.precision === "year") return date.value.slice(0, 4);
  return formatShortDate(`${date.value.slice(0, 10)}T12:00:00.000Z`);
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="m-0 grid grid-cols-[104px_1fr] gap-x-3 gap-y-1.5 text-ui [&_dd]:m-0 [&_dd]:min-w-0 [&_dd]:text-fg [&_dt]:text-muted">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatClock(ms: number | null): string {
  return ms === null ? "" : ` ${formatDuration(Math.round(ms / 1000))}`;
}

function sizeText(bytes: number | null): string {
  if (bytes === null) return "size not stated";
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

function episodeTypeLabel(type: string): string {
  if (type === "trailer") return "Trailer (preview)";
  return type === "bonus" ? "Bonus" : "Full episode";
}

function FeedSection({ feed }: { feed: NonNullable<ItemDetail["feed"]> }) {
  const rows: [string, ReactNode][] = [];
  if (feed.enclosure_url) {
    rows.push([
      "File",
      <a
        key="f"
        href={feed.enclosure_url}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1 text-link"
      >
        {new URL(feed.enclosure_url).pathname.split("/").pop() || feed.enclosure_url} · {sizeText(feed.enclosure_bytes)}
        <ExternalLink size={11} aria-hidden="true" />
      </a>,
    ]);
  }
  if (feed.episode_type) rows.push(["Episode type", episodeTypeLabel(feed.episode_type)]);
  if (feed.guests.length > 0) rows.push(["Guests", feed.guests.join(", ")]);
  if (feed.available_until)
    rows.push([
      "Available until",
      <span key="a" title={formatDateTime(feed.available_until)}>
        {formatShortDate(feed.available_until)} · collected ahead of other work
      </span>,
    ]);
  return (
    <DrawerSection title="Feed entry">
      <Facts rows={rows} />
      {feed.changes.length > 0 ? (
        <ul className="m-0 mt-1.5 list-none p-0 text-md" aria-label="Publisher changes">
          {feed.changes.map((c) => (
            <li key={`${c.kind}:${c.at}`}>
              <span className="font-strong">{c.label}</span> ·{" "}
              <span title={formatDateTime(c.at)}>{formatRelative(c.at)}</span>
              {c.note ? <span className="text-muted"> · {c.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </DrawerSection>
  );
}

function NewsSection({ news }: { news: NonNullable<ItemDetail["news"]> }) {
  return (
    <DrawerSection title="News report" note={news.text_basis_label ?? "Publisher's page not captured yet"}>
      <Facts
        rows={[
          ["Publisher", news.source],
          ["Byline", news.authors.length > 0 ? news.authors.join(", ") : "Not stated"],
          ["Found through", `${news.supplier} news search`],
          ...(news.named_originals.length > 0
            ? ([
                [
                  "Original named",
                  <ul key="o" aria-label="Originals named in the report" className="m-0 list-none p-0">
                    {news.named_originals.map((o) => (
                      <li key={o.text}>
                        {o.item_id ? (
                          <AppLink href={`/backlog/${o.item_id}`} className="text-link">
                            {o.text}
                          </AppLink>
                        ) : (
                          o.text
                        )}
                      </li>
                    ))}
                  </ul>,
                ],
              ] as [string, ReactNode][])
            : []),
          ...(news.links.length > 0
            ? ([
                [
                  "Links in the report",
                  <ul key="l" className="m-0 list-none p-0">
                    {news.links.map((l) => (
                      <li key={l} className="break-all">
                        {l}
                      </li>
                    ))}
                  </ul>,
                ],
              ] as [string, ReactNode][])
            : []),
        ]}
      />
    </DrawerSection>
  );
}

function archivedMediaLabel(kind: string): string {
  if (kind === "photo") return "Photo";
  return kind === "animated_gif" ? "GIF" : "Video";
}

type XView = NonNullable<ItemDetail["x"]>;

function ReplyParent({ parent }: { parent: NonNullable<XView["replying_to"]> }) {
  return (
    <div className="mb-2 flex flex-col gap-1 border-l-2 border-divider pl-2 text-md text-muted">
      <span className="flex items-baseline gap-2">
        <span className="min-w-0">
          Replying to {parent.author ? `${parent.author.name} (@${parent.author.handle})` : "a post"}
        </span>
        {parent.created_at ? (
          <span className="shrink-0 whitespace-nowrap" title={formatDateTime(parent.created_at)}>
            {formatShortDate(parent.created_at)}
          </span>
        ) : null}
        <a
          href={parent.url}
          target="_blank"
          rel="noreferrer noopener"
          className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-link"
        >
          On X
          <ExternalLink size={11} aria-hidden="true" />
        </a>
      </span>
      {parent.text !== null ? (
        <p className="m-0 whitespace-pre-wrap break-words text-fg">{parent.text}</p>
      ) : (
        <p className="m-0">{parent.note}</p>
      )}
    </div>
  );
}

function XThreadSection({ x }: { x: XView }) {
  const count = x.posts.length;
  return (
    <DrawerSection title={count > 1 ? `Thread · ${String(count)} posts` : "Post"} note={x.text_basis_label}>
      {x.replying_to ? <ReplyParent parent={x.replying_to} /> : null}
      <ol
        className="m-0 list-none divide-y divide-divider rounded-lg border border-divider p-0"
        aria-label={count > 1 ? "Posts in order" : "Post"}
      >
        {x.posts.map((p, i) => (
          <li key={p.post_id} className="flex flex-col gap-1 px-3 py-2">
            <span className="flex items-baseline gap-2 text-md text-muted">
              {count > 1 ? (
                <span className="font-mono text-xs">
                  {String(i + 1)}/{String(count)}
                </span>
              ) : null}
              <span title={formatDateTime(p.created_at)}>{formatShortDate(p.created_at)}</span>
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer noopener"
                className="ml-auto inline-flex items-center gap-1 text-link"
              >
                On X
                <ExternalLink size={11} aria-hidden="true" />
              </a>
            </span>
            <p className="m-0 whitespace-pre-wrap break-words">{p.text}</p>
            {p.media.length > 0 ? (
              <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-0.5 p-0 text-md text-muted">
                {p.media.map((m) => (
                  <li key={m.url}>
                    {m.archived && m.artifact_id ? (
                      <a
                        href={`/api/collection/artifacts/${m.artifact_id}/content`}
                        className="text-link"
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {archivedMediaLabel(m.kind)}
                        {formatClock(m.duration_ms)} · archived
                      </a>
                    ) : (
                      <span>
                        {m.kind === "photo" ? "Photo" : "Video"} · {m.note ?? "not archived"}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            {p.quotes ? (
              <div className="border-l-2 border-divider pl-2 text-md text-muted">
                Quotes @{p.quotes.author_handle}
                {p.quotes.item_id ? (
                  <>
                    {" · "}
                    <AppLink href={`/backlog/${p.quotes.item_id}`} className="text-link">
                      open the original
                    </AppLink>
                  </>
                ) : null}
                <p className="m-0 line-clamp-2 text-fg">{p.quotes.text}</p>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      {!x.thread_complete ? (
        <p className="m-0 mt-1 text-md text-waiting">
          Later posts may be missing: the supplier's thread was read to its page limit.
        </p>
      ) : null}
      {x.page_capture.state === "failed" ? (
        <p className="m-0 mt-1 text-md text-muted">{x.page_capture.detail}</p>
      ) : null}
    </DrawerSection>
  );
}

const REASON_TONE = { failed: "attention", waiting: "waiting", excluded: "neutral" } as const;

function ReasonCallout({ detail }: { detail: ItemDetail }) {
  const s = detail.item.status;
  if (s.status !== "failed" && s.status !== "waiting" && s.status !== "excluded") return null;
  return (
    <Callout tone={REASON_TONE[s.status]} title={s.reason ?? s.title}>
      {s.status === "failed" ? <span className="block">{s.precise_label}</span> : null}
      {s.attempts && s.status !== "failed" ? (
        <span className="block">{`${String(s.attempts.used)} of ${String(s.attempts.allowed)} attempts`}</span>
      ) : null}
      {s.stopped ? (
        <span className="block">
          Retrying stopped by {s.stopped.by}, {formatDateTime(s.stopped.at)}
        </span>
      ) : null}
      {s.next ? (
        <span className="block">
          Next: {s.next}
          {s.next_at ? `, ${formatDateTime(s.next_at)}` : ""}
        </span>
      ) : null}
    </Callout>
  );
}

function ExcludeDialog({ detail, onClose }: { detail: ItemDetail; onClose: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<ExclusionKind>("not_relevant");
  const mutation = useCommandMutation({
    run: (csrf, reason: string) =>
      collectionCommands.exclude(detail.item.id, { kind, reason, base_revision: detail.item.revision }, csrf),
    invalidate: AFTER_COLLECTION_CHANGE,
    invalidateOnConflict: ON_COLLECTION_CONFLICT,
    onSuccess: () => {
      toast.show("Item excluded");
      onClose();
    },
  });
  const serverReason = isApiError(mutation.error) ? mutation.error.fieldErrors()["reason"] : undefined;
  const reason = useReason(serverReason);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Exclude item"
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter onClose={onClose} confirm="Exclude" pending={mutation.isPending} failed={mutation.isError} />
      }
    >
      <DialogText>
        <b>{detail.item.title}</b> stops before its next stage. Everything already captured is kept, and an Editor can
        reconsider it later.
      </DialogText>
      <RadioCards label="Why" value={kind} options={EXCLUSION_OPTIONS} onChange={setKind} />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      {serverReason ? null : <FailedCallout error={mutation.error} />}
    </Dialog>
  );
}

function ReconsiderDialog({ detail, onClose }: { detail: ItemDetail; onClose: () => void }) {
  return (
    <ReasonDialog
      title="Reconsider item"
      confirm="Reconsider"
      success="Item returned as New"
      onClose={onClose}
      run={(reason, csrf) =>
        collectionCommands.reconsider(detail.item.id, { reason, base_revision: detail.item.revision }, csrf)
      }
      invalidate={AFTER_COLLECTION_CHANGE}
      invalidateOnConflict={ON_COLLECTION_CONFLICT}
    >
      <b>{detail.item.title}</b> re-enters as New and goes through every stage again. The exclusion (
      {detail.exclusion?.label ?? "excluded"}) stays in its history.
    </ReasonDialog>
  );
}

type ItemDialog = "exclude" | "reconsider" | "retry" | "stop" | "reprocess";

function ItemDrawerFooter({ detail, onDialog }: { detail: ItemDetail; onDialog: (dialog: ItemDialog) => void }) {
  const item = detail.item;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <AppLink href={`/items/${item.id}?from=backlog`} className={buttonClass("primary", "sm")}>
        Open record →
      </AppLink>
      {detail.exclusion !== null ? (
        <ActionButton
          size="sm"
          action={detail.actions.reconsider}
          label="Reconsider…"
          onClick={() => {
            onDialog("reconsider");
          }}
        />
      ) : (
        <ActionButton
          size="sm"
          action={detail.actions.exclude}
          label="Exclude…"
          onClick={() => {
            onDialog("exclude");
          }}
        />
      )}
      {detail.engine_run ? (
        <a
          href={detail.engine_run.href}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 text-md text-link"
        >
          Open engine run
          <ExternalLink size={12} aria-hidden="true" />
        </a>
      ) : null}
      <a
        href={item.original_url}
        target="_blank"
        rel="noreferrer noopener"
        className="ml-auto inline-flex items-center gap-1 text-md text-link"
      >
        Original
        <ExternalLink size={12} aria-hidden="true" />
      </a>
    </span>
  );
}

function ItemBadges({ item }: { item: ItemDetail["item"] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <StatusBadge status={item.status} />
      {item.readiness ? <ReadinessCell readiness={item.readiness} /> : null}
      {item.added_by_link ? <span className="text-md text-muted">Added by link</span> : null}
      {item.access_state !== "normal" ? (
        <span className="text-md text-attention">
          {item.access_state === "restricted" ? "Restricted" : "Deleted"} · visible to Admins only
        </span>
      ) : null}
    </div>
  );
}

function dateFacts(detail: ItemDetail): [string, ReactNode][] {
  const { event, published, conflicts } = detail.dates;
  const known = isPublicationProxy(event) ? null : event;
  const rows: [string, ReactNode][] = [
    ["Published", `${formatSourceDate(published)}${published && !known ? " (used as date)" : ""}`],
  ];
  if (known) rows.push(["Occurred", `${formatSourceDate(known)} · ${SOURCE_DATE_BASIS_LABELS[known.basis]}`]);
  else if (event) rows.push(["Occurred", "Unknown"]);
  if (conflicts.length > 0) rows.push(["Date conflict", <DateConflictList key="conflicts" conflicts={conflicts} />]);
  return rows;
}

function ItemFacts({ detail }: { detail: ItemDetail }) {
  const item = detail.item;
  const media = [
    MEDIA_TYPE_LABELS[item.media_type],
    item.duration_seconds === null ? null : formatDuration(item.duration_seconds),
    item.size_bytes === null ? null : formatBytes(item.size_bytes),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Facts
      rows={[
        ["Platform", item.platform.label],
        ["Channel", item.channel ?? "—"],
        ...dateFacts(detail),
        [
          "Discovered",
          <span key="d" title={formatDateTime(item.discovered_at)}>
            {formatRelative(item.discovered_at)}
          </span>,
        ],
        ["Media", media],
        ["Language", item.language?.toUpperCase() ?? "Detected after download"],
        ["Selected", detail.selection_label],
        ...(detail.form
          ? ([["Form", detail.form === "standalone" ? "Standalone post" : ITEM_FORM_LABELS[detail.form]]] as [
              string,
              ReactNode,
            ][])
          : []),
        ...detail.references.map((r): [string, ReactNode] => [
          r.label,
          <AppLink key={r.item_id} href={`/backlog/${r.item_id}`} className="text-link">
            {r.title}
          </AppLink>,
        ]),
      ]}
    />
  );
}

const STAGE_CLASS: Partial<Record<string, string>> = {
  failed: "flex-1 text-attention",
  waiting: "flex-1 text-waiting",
};

function StagesSection({ detail }: { detail: ItemDetail }) {
  return (
    <DrawerSection title="Stages" note={`total ${formatCost(detail.total_cost.amount_decimal)}`}>
      <ol className="m-0 list-none divide-y divide-divider rounded-lg border border-divider p-0">
        {detail.stages.map((s) => (
          <li key={s.stage} className="flex items-baseline gap-2 px-3 py-1.5">
            <span className="w-[86px] flex-none font-strong">{s.name}</span>
            <span className={STAGE_CLASS[s.state] ?? "flex-1 text-muted"}>{s.text}</span>
            {s.cost ? <span className="font-mono text-xs text-muted">{formatCost(s.cost.amount_decimal)}</span> : null}
          </li>
        ))}
      </ol>
    </DrawerSection>
  );
}

function ExclusionHistorySection({ history }: { history: ItemDetail["exclusion_history"] }) {
  if (history.length === 0) return null;
  return (
    <DrawerSection title="Earlier exclusions">
      <ul className="m-0 list-none p-0 text-md">
        {history.map((h) => (
          <li key={h.at} className="border-l-2 border-divider py-1 pl-3">
            {h.label} · {h.by}, {formatShortDate(h.at)} · “{h.reason}”
            <div className="text-muted">
              Reconsidered by {h.cleared_by}, {formatShortDate(h.cleared_at)}
              {h.cleared_reason ? ` · “${h.cleared_reason}”` : ""}
            </div>
          </li>
        ))}
      </ul>
    </DrawerSection>
  );
}

function ManualActionsSection({ detail }: { detail: ItemDetail }) {
  return (
    <DrawerSection title="Manual actions">
      {detail.manual_actions.length === 0 ? (
        <p className="m-0 text-muted">None</p>
      ) : (
        <ol className="m-0 list-none p-0 text-md">
          {detail.manual_actions.map((a) => (
            <li key={a.id} className="border-l-2 border-divider py-1 pl-3">
              <span className="font-strong">{a.label}</span> · {a.by}, {formatDateTime(a.at)}
              {a.reason ? <div className="text-muted">“{a.reason}”</div> : null}
            </li>
          ))}
        </ol>
      )}
      {detail.manual_actions_total > detail.manual_actions.length ? (
        <AppLink href="/settings/audit?subject=item" className="mt-1.5 inline-block text-md text-link">
          All entries in the audit log
        </AppLink>
      ) : null}
    </DrawerSection>
  );
}

function PeopleChips({ people }: { people: ItemDetail["item"]["people"] }) {
  if (people.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {people.map((p) => (
        <span
          key={p.id}
          className="inline-flex items-center gap-1 rounded-full border border-divider py-px pr-2 pl-0.5 text-md"
        >
          <Avatar name={p.name} />
          {p.name}
        </span>
      ))}
    </div>
  );
}

function ItemDialogs({
  dialog,
  detail,
  onClose,
}: {
  dialog: ItemDialog | null;
  detail: ItemDetail;
  onClose: () => void;
}) {
  return (
    <>
      {dialog === "exclude" ? <ExcludeDialog detail={detail} onClose={onClose} /> : null}
      {dialog === "reconsider" ? <ReconsiderDialog detail={detail} onClose={onClose} /> : null}
      {dialog === "retry" ? <RetryDialog detail={detail} onClose={onClose} /> : null}
      {dialog === "stop" ? <StopDialog detail={detail} onClose={onClose} /> : null}
      {dialog === "reprocess" ? (
        <RecoveryDialog
          ids={[detail.item.id]}
          title="Reprocess this item?"
          initialMode="reprocess"
          onClose={onClose}
          onQueued={onClose}
        />
      ) : null}
    </>
  );
}

function ItemDrawerPending({ error, isError, onClose }: { error: unknown; isError: boolean; onClose: () => void }) {
  return (
    <Drawer title={isError ? "Item not found" : "Loading item…"} onClose={onClose}>
      {isError ? (
        <p className="m-0 text-muted">
          {isApiError(error) && error.status === 404
            ? "No item with this ID, or it isn't visible to your role."
            : errorText(error)}
        </p>
      ) : null}
    </Drawer>
  );
}

export function ItemDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const query = useQuery(itemQuery(id));
  const [dialog, setDialog] = useState<ItemDialog | null>(null);
  const detail = query.data;
  if (!detail) return <ItemDrawerPending error={query.error} isError={query.isError} onClose={onClose} />;
  const item = detail.item;
  return (
    <Drawer
      title={item.title}
      subtitle={item.people.length > 0 ? item.people.map((p) => p.name).join(", ") : <span>No tracked person</span>}
      onClose={onClose}
      footer={<ItemDrawerFooter detail={detail} onDialog={setDialog} />}
    >
      <div className="flex flex-col gap-4 text-ui">
        <ItemBadges item={item} />
        <ReasonCallout detail={detail} />
        {item.status.status === "failed" ? (
          <RecoverySection
            detail={detail}
            onRetry={() => {
              setDialog("retry");
            }}
            onStop={() => {
              setDialog("stop");
            }}
            onReprocess={() => {
              setDialog("reprocess");
            }}
          />
        ) : null}
        <DrawerSection title="Pipeline">
          <ProgressBar item={item} large />
          <ProgressLabels item={item} />
          <p className="m-0 mt-1.5 font-strong">{item.stage_detail}</p>
        </DrawerSection>
        <ItemFacts detail={detail} />
        {detail.x ? <XThreadSection x={detail.x} /> : null}
        {detail.feed ? <FeedSection feed={detail.feed} /> : null}
        {detail.news ? <NewsSection news={detail.news} /> : null}
        {detail.added ? (
          <DrawerSection title="Added by link">
            <p className="m-0">
              {detail.added.by}, {formatDateTime(detail.added.at)} · “{detail.added.reason}”
            </p>
          </DrawerSection>
        ) : null}
        <StagesSection detail={detail} />
        <ExclusionHistorySection history={detail.exclusion_history} />
        <ManualActionsSection detail={detail} />
        <PeopleChips people={item.people} />
      </div>
      <ItemDialogs
        dialog={dialog}
        detail={detail}
        onClose={() => {
          setDialog(null);
        }}
      />
    </Drawer>
  );
}
